/* eslint-disable no-param-reassign */
import { inject, getCurrentInstance } from 'vue';
import { ENV } from '@src/env';

const migratedList = ['SbStepper'];

const VersionOptions = {
  LEGACY: 'legacy',
  NEXT: 'next',
};

const logDeprecationAlert = ({
  customMessage,
  componentName,
}) => {
  if (ENV.SHOW_NEXT_VERSION_WARNING) {
    let message;

    if (!componentName) {
      componentName = getCurrentInstance()?.type?.name;
    }

    if (customMessage) {
      message = customMessage;
    } else {
      message = `${componentName} has a new version available, please migrate to the next version. Checkout the migration guides for more information: http://designsystem.shipbob.com/?path=/story/next-migration-guide--page&globals=locale:en-US`;
    }

    // eslint-disable-next-line no-console
    console.log(`%c ⚠️ ${message}`, 'color: white; background: #D22B2B; font-size: 16px; border-radius: 5px; padding: 5px;');
  }
};

const componentVersion = ({
  componentVersions,
  componentVersionPropValue,
  componentName,
}) => (componentVersions === VersionOptions.NEXT
  || componentVersions?.[componentName] === VersionOptions.NEXT
  || componentVersionPropValue === VersionOptions.NEXT
  ? VersionOptions.NEXT : VersionOptions.LEGACY);

const getComponentDetails = ({
  componentVersions,
  componentVersionPropValue,
  componentName,
}) => {
  const instance = getCurrentInstance();

  if (!componentName) {
    componentName = instance?.type?.name;
  }

  const parent = instance?.parent;
  const hasShipbobParent = !!parent?.type?.name?.startsWith('Sb') && !!parent?.components?.[componentName];
  const shipbobParentHasBeenMigrated = !!parent?.type?.name?.startsWith('Sb') && (!!parent?.props?.version || migratedList.includes(parent?.type?.name));
  const componentDetails = {
    version: undefined,
    parentVersion: undefined,
    hasShipbobParent,
    shipbobParentHasBeenMigrated,
  };
  const props = instance?.props;

  if (props?.version !== undefined) {
    componentDetails.version = componentVersion({
      componentVersions: props.version,
      componentVersionPropValue: parent.props.version,
      componentName: parent.type.name,
    });
  } else if (hasShipbobParent && shipbobParentHasBeenMigrated) {
    componentDetails.version = componentVersion({
      componentVersions,
      componentVersionPropValue: parent.props.version,
      componentName: parent.type.name,
    });
    componentDetails.parentVersion = parent.props.version;
  } else if (hasShipbobParent && !shipbobParentHasBeenMigrated) {
    componentDetails.version = componentVersionPropValue || VersionOptions.LEGACY;
    componentDetails.parentVersion = VersionOptions.LEGACY;
  } else {
    componentDetails.version = componentVersion({
      componentVersions,
      componentVersionPropValue,
      componentName,
    });
  }

  return componentDetails;
};

const shouldShowDeprecationAlert = ({
  componentVersions,
  componentVersionPropValue,
  componentName,
  newComponent,
  customMessage,
}) => {
  const componentDetails = getComponentDetails({
    componentVersions,
    componentVersionPropValue,
    componentName,
  });

  const {
    version,
    hasShipbobParent,
    shipbobParentHasBeenMigrated,
  } = componentDetails;

  const isCurrentVersion = version === VersionOptions.LEGACY;
  const parentHasNotBeenMigrated = hasShipbobParent && !shipbobParentHasBeenMigrated;
  const noParentComponent = !hasShipbobParent;

  if ((!parentHasNotBeenMigrated || noParentComponent) && isCurrentVersion) {
    logDeprecationAlert({
      customMessage,
      componentName,
    });
  }

  return componentDetails;
};

const versionedComponentsMixin = {
  inject: ['componentVersions'],
  props: {
    /**
     * The version of the component. Defaults to legacy.
     * `legacy, next`
     */
    version: {
      type: String,
      validator: (value) => {
        return value.match(/(legacy|next)/);
      },
    },
  },
  data() {
    return {
      customMessage: null,
      dsComponentDetails: {
        version: null,
        parentVersion: null,
        hasShipbobParent: false,
        shipbobParentHasBeenMigrated: false,
      },
      VersionOptions,
    };
  },
  created() {
    this.dsComponentDetails = shouldShowDeprecationAlert({
      componentVersions: this.componentVersions,
      componentVersionPropValue: this.version,
      componentName: this.$options.name,
      customMessage: this.customMessage,
    });
  },
};

const useVersionedComponents = ({
  customMessage = null,
  componentVersionPropValue = null,
}) => {
  const componentVersions = inject('componentVersions');
  const dsComponentDetails = shouldShowDeprecationAlert({
    componentVersions,
    componentVersionPropValue,
    customMessage,
  });

  return {
    dsComponentDetails,
    versionedComponentProps: versionedComponentsMixin.props,
  };
};

export {
  versionedComponentsMixin,
  useVersionedComponents,
  VersionOptions,
};
